import { set } from "react-ga";
import {
  type RemoveMailsActionPayloadI,
  type RemoveMailForeverPayloadI,
} from "./../store/thunks/inboxThunks";
import type { LabelT, MailDataI } from "../models/models";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  selectAllSearchParams,
  selectApiEnable,
  selectCurrentPage,
  selectCurrentSelectFilter,
  selectFormList,
  selectFtpEnable,
  selectHoveredMail,
  selectHoveredMailId,
  selectIsFetching,
  selectIsFetchingSideInboxFiles,
  selectIsSortAsc,
  selectLabels,
  selectMailsCount,
  selectMailsData,
  selectMailsQuantity,
  selectPreviewMail,
  selectSearchParams,
  selectSelectedList,
  selectSelectedMail,
  selectSelectedMailId,
  selectSelectedMailIds,
  selectSideInboxFiles,
  selectSortBy,
  selectSplitView,
  selectSubUserList,
} from "../store/selectors/inboxSelectors";
import {
  setSorting as setSortingAction,
  setHoveredMail as setHoveredMailAction,
  setHoveredMailId as setHoveredMailIdAction,
  setSelectedMail as setSelectedMailAction,
  setSearchParams as setSearchParamsAction,
  setCurrentPage as setCurrentPageAction,
  setIsFetching as setIsFetchingAction,
  setSelectedMailIds as setSelectedListAction,
  removeMailFromState as removeMailFromStateAction,
  setCurrentSelectFilter as setCurrentSelectFilterAction,
  setSplitView as setSplitViewAction,
  setPreviewMail as setPreviewMailAction,
  setIsFetchingSideInboxFiles as setIsFetchingSideInboxFilesAction,
  setMailsCount as setMailsCountAction,
  setLabels as setLabelsAction,
  setLabel as setLabelAction,
  setSelectedMailId as setSelectedMailIdAction,
  setPickerSearchParams as setPickerSearchParamsAction,
  setMailIsSaved as setMailIsSavedAction,
  setMailStatus as setMailStatusAction,
  setMailsData,
  type MailsCountI,
  type SearchParamsI,
  setPageMailType,
  setPageMailCategory,
  setItemsPerPage as setItemsPerPageAction,
  inboxInitialState,
} from "../store/slices/inboxSlice";
import {
  fetchInboxData as fetchInboxDataThunk,
  fetchMailsCount as fetchMailsCountThunk,
  updateMailSecuritySettings as updateMailSecuritySettingsThunk,
  updateMailPayments as updateMailPaymentsThunk,
  removeMailsAction as removeMailsActionThunk,
  removeMailForever as removeMailForeverThunk,
  fetchSideInboxFiles as fetchSideInboxFilesThunk,
  fetchFormList as fetchFormListThunk,
  fetchLabels as fetchLabelsThunk,
  createLabel as createLabelThunk,
  updateLabel as updateLabelThunk,
  deleteLabel as deleteLabelThunk,
  labelMails as labelMailsThunk,
  unlabelMails as unlabelMailsThunk,
  fetchSubUserList as fetchSubUserListAction,
} from "../store/thunks/inboxThunks";
import {
  type ReactGetSideInboxFilesApiRequestPayloadT,
  type ReactUpdateSecuritySettingsApiRequestPayloadT,
} from "../api/MailsController";
import { type RootState } from "../store/store";

const PAGE_SIZE = 10;
const FIRST_PAGE_NUM = 1;

const useInbox = () => {
  const dispatch = useAppDispatch();

  const mailsData = useAppSelector(selectMailsData);
  const getMailsData = () => mailsData;

  const mailsQuantity = useAppSelector(selectMailsQuantity);
  const getMailsQuantity = () => mailsQuantity;

  const isFetching = useAppSelector(selectIsFetching);
  const getIsFetching = () => isFetching;

  const setIsFetching = (value: boolean) => {
    dispatch(setIsFetchingAction(value));
  };

  const sortBy = useAppSelector(selectSortBy);
  const isSortAsc = useAppSelector(selectIsSortAsc);
  const getSorting = () => ({
    by: sortBy,
    asc: isSortAsc,
  });

  const setSorting = (sortObj: {
    by: RootState["inbox"]["searchParams"]["sortBy"];
    asc: boolean;
  }) => {
    dispatch(
      setSortingAction({
        sortBy: sortObj.by,
        isSortAsc: sortObj.asc,
      })
    );

    // Update sorting params in the URL without page rerendering
    const params = new URLSearchParams(window.location.search);
    params.set("sort", sortObj.by);
    params.set("asc", String(sortObj.asc));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const hoveredMail = useAppSelector(selectHoveredMail);
  const getHoveredMail = () => hoveredMail;

  const hoveredMailId = useAppSelector(selectHoveredMailId);

  /**
   * @deprecated Use setHoveredMaiId instead
   */
  const setHoveredMail = (mail: MailDataI | null) => {
    dispatch(setHoveredMailAction(mail));
  };

  const setHoveredMailId = (encryptedMailId: string | null) => {
    dispatch(setHoveredMailIdAction(encryptedMailId));
  };

  const selectedMail = useAppSelector(selectSelectedMail);
  const getSelectedMail = () => selectedMail;

  /**
   * Id of the mail that is currently selected and displayed in the split view
   */
  const selectedMailId = useAppSelector(selectSelectedMailId);

  /**
   * @deprecated Use setSelectedMailId instead
   */
  const setSelectedMail = (mail: MailDataI | null) => {
    dispatch(setSelectedMailAction(mail));
  };

  const setSelectedMailId = (encryptedMailId: string) => {
    dispatch(setSelectedMailIdAction(encryptedMailId));
  };

  const searchParams = useAppSelector(selectSearchParams);
  const getSearchParams = () => searchParams;

  // Apply search params for the current t (mail type) and f (mail category) parameters
  const setSearchParams = (searchParams: SearchParamsI) => {
    dispatch(setSearchParamsAction(searchParams));

    // Update search params in the URL without page rerendering
    const params = new URLSearchParams(window.location.search);
    for (const param in searchParams) {
      const value = searchParams[param as keyof SearchParamsI];
      if (value !== undefined) {
        if (!value || !value?.length) {
          params.delete(param);
        } else {
          params.set(param, Array.isArray(value) ? value.join(",") : value);
        }
      }
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const setPickerSearchParams = (
    searchParams: Partial<RootState["inbox"]["searchParams"]["picker"]>
  ) => {
    dispatch(
      setPickerSearchParamsAction({
        ...searchParams,
      })
    );

    if (allSearchParams.t === "picker") {
      const params = new URLSearchParams(window.location.search);
      for (const param in searchParams) {
        const value =
          searchParams[
            param as keyof RootState["inbox"]["searchParams"]["picker"]
          ];
        if (value !== undefined && value?.length) {
          params.set(param, Array.isArray(value) ? value.join(",") : value);
        } else {
          params.delete(param);
        }
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({}, "", newUrl);
    }
  };

  const currentPage = useAppSelector(selectCurrentPage);
  const getCurrentPage = () => +currentPage;

  const incPageNumber = () => {
    if (mailsQuantity! < currentPage * PAGE_SIZE || isFetching) {
      return;
    }
    dispatch(setCurrentPageAction(+currentPage + 1));
  };

  const decPageNumber = () => {
    if (+currentPage === 1 || isFetching) {
      return;
    }
    dispatch(setCurrentPageAction(+currentPage - 1));
  };

  const setPageNumber = (pageNumber: number) => {
    dispatch(setCurrentPageAction(+pageNumber));

    // Update page number in the URL without page rerendering
    const params = new URLSearchParams(window.location.search);
    params.set("pn", String(pageNumber));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const resetCurrentPage = () => {
    dispatch(setCurrentPageAction(FIRST_PAGE_NUM));
  };

  const fetchInboxData = async (userId: string) =>
    await dispatch(fetchInboxDataThunk(userId)).unwrap();

  const fetchMailsCount = async () =>
    await dispatch(fetchMailsCountThunk()).unwrap();

  const fetchInboxMailsCount = fetchMailsCount;

  /**
   * @deprecated Use selectedMailIds instead
   */
  const selectedList = useAppSelector(selectSelectedList);
  /**
   * @deprecated Use selectedMailIds instead
   */
  const getSelectedList = () => selectedList;

  /**
   * @deprecated Use selectedMailIds instead
   */
  const setSelectedList = (list: MailDataI[]) => {
    dispatch(setSelectedListAction(list));
  };

  const selectedMailIds = useAppSelector(selectSelectedMailIds);

  const setSelectedMailIds = (encryptedMailIds: string[]) => {
    dispatch(setSelectedListAction(encryptedMailIds));
  };

  const setMailDataProp = (
    id: string,
    propsArray: (keyof MailDataI)[],
    valueArray: any[]
  ) => {
    const updatedMail: MailDataI = { ...mailsData[id] };
    propsArray.forEach((prop, i) => {
      //TODO: provide type instead of any
      (updatedMail[prop] as any) = valueArray[i];
    });
    dispatch(
      setMailsData({
        ...mailsData,
        [id]: updatedMail,
      })
    );
  };

  const updateMailSecuritySettings = async (
    body: Omit<ReactUpdateSecuritySettingsApiRequestPayloadT, "mailId">,
    mailId: string,
    callback: () => void,
    setSecurity: (security: MailDataI["security"]) => void
  ) =>
    await dispatch(
      updateMailSecuritySettingsThunk({
        body,
        mailId,
        callback,
        setSecurity,
      })
    ).unwrap();

  const updateMailPayments = async (
    body: {
      PaymentAmount: number;
      DisplayName: string;
      PaymentOnce: boolean;
      PreviewItems: boolean;
      recaptchaResponse: string;
    },
    mailId: string,
    callback: () => void
  ) =>
    await dispatch(
      updateMailPaymentsThunk({
        body,
        mailId,
        callback,
      })
    ).unwrap();

  const removeMailsAction = async (payload: RemoveMailsActionPayloadI) =>
    await dispatch(removeMailsActionThunk(payload)).unwrap();

  const removeMailForever = async (payload: RemoveMailForeverPayloadI) =>
    await dispatch(removeMailForeverThunk(payload)).unwrap();

  const removeMailFromState = (mailId: string) => {
    dispatch(removeMailFromStateAction(mailId));
  };

  const currentSelectFilter = useAppSelector(selectCurrentSelectFilter);
  const getCurrentSelectFilter = () => currentSelectFilter;

  const setCurrentSelectFilter = (filter: string) => {
    dispatch(setCurrentSelectFilterAction(filter));
  };

  const apiEnable = useAppSelector(selectApiEnable);
  const getApiEnable = () => apiEnable;

  const ftpEnable = useAppSelector(selectFtpEnable);
  const getFtpEnable = () => ftpEnable;

  const getFTPAPIsettings = () => {
    return { api: apiEnable, ftp: ftpEnable };
  };

  const splitView = useAppSelector(selectSplitView);
  const getSplitView = () => splitView;

  const setSplitView = (value: boolean) => {
    dispatch(setSplitViewAction(value));
  };

  const previewMail = useAppSelector(selectPreviewMail);
  const getPreviewMail = () => previewMail;

  const setPreviewMail = (mailId: string) => {
    dispatch(setPreviewMailAction(mailId));
  };

  const getPreviewMailData = () => mailsData?.[previewMail as keyof MailDataI];

  const isFetchingSideInboxFiles = useAppSelector(
    selectIsFetchingSideInboxFiles
  );
  const getIsFetchingSideInboxFiles = () => isFetchingSideInboxFiles;

  const setIsFetchingSideInboxFiles = (value: boolean) => {
    dispatch(setIsFetchingSideInboxFilesAction(value));
  };

  const fetchSideInboxFiles = async (
    payload: ReactGetSideInboxFilesApiRequestPayloadT
  ) => {
    await dispatch(fetchSideInboxFilesThunk(payload)).unwrap();
  };

  const sideInboxFiles = useAppSelector(selectSideInboxFiles);
  const getSideInboxFiles = () => sideInboxFiles;

  const mailsCount = useAppSelector(selectMailsCount);
  const getMailsCount = () => mailsCount;

  const setMailsCount = ({
    received,
    sent,
    all,
    trash,
    jmail,
    jform,
    jpay,
  }: Omit<MailsCountI, "inbox" | "outbox" | "labels">) => {
    const newMailsCountPayload = {
      ...mailsCount,
      received: (mailsCount?.received || 0) + (received || 0),
      sent: (mailsCount?.sent || 0) + (sent || 0),
      all: (mailsCount?.all || 0) + (all || 0),
      trash: (mailsCount?.trash || 0) + (trash || 0),
      jmail: (mailsCount?.jmail || 0) + (jmail || 0),
      jform: (mailsCount?.jform || 0) + (jform || 0),
      jpay: (mailsCount?.jpay || 0) + (jpay || 0),
    };
    dispatch(setMailsCountAction(newMailsCountPayload));
  };

  const formList = useAppSelector(selectFormList);
  const getFormList = () => formList;

  const fetchFormList = async () => {
    await dispatch(fetchFormListThunk()).unwrap();
  };

  const labels = useAppSelector(selectLabels);
  const getLabels = () => labels;

  const setLabels = (labels: Record<number, LabelT>) => {
    dispatch(setLabelsAction(labels));
  };

  const setLabel = (label: LabelT) => {
    dispatch(setLabelAction(label));
  };

  const fetchLabels = async () => {
    await dispatch(fetchLabelsThunk()).unwrap();
  };

  const createLabel = async (name: string, color: string) => {
    await dispatch(createLabelThunk({ name, color })).unwrap();
  };

  const updateLabel = async (data: {
    id: number;
    name: string;
    color: string;
  }) => {
    await dispatch(updateLabelThunk(data)).unwrap();
  };

  const deleteLabel = async (id: number) => {
    await dispatch(deleteLabelThunk({ id })).unwrap();
  };

  const updateLabelsCounters = (labelIds: number[]) => {
    labelIds.forEach((labelId) => {
      if (mailsCount?.labels) {
        if (mailsCount.labels.hasOwnProperty(labelId)) {
          dispatch(
            setMailsCountAction({
              ...mailsCount,
              labels: {
                ...mailsCount.labels,
                [labelId]: mailsCount.labels[labelId as unknown as number] + 1,
              },
            })
          );
        } else {
          dispatch(
            setMailsCountAction({
              ...mailsCount,
              labels: {
                ...mailsCount.labels,
                [labelId]: 1,
              },
            })
          );
        }
      }
    });
  };

  const labelMails = async (encryptedMailIds: string[], labelIds: number[]) => {
    await dispatch(labelMailsThunk({ encryptedMailIds, labelIds })).unwrap();
  };

  const unlabelMails = async (
    encryptedMailIds: string[],
    labelIds: number[]
  ) => {
    await dispatch(unlabelMailsThunk({ encryptedMailIds, labelIds })).unwrap();
  };

  const fetchSubUserList = async () => {
    await dispatch(fetchSubUserListAction()).unwrap();
  };

  const subUsersList = useAppSelector(selectSubUserList);
  const getSubUserList = () => subUsersList;

  const refetchInboxData = () => {
    decPageNumber();
  };

  const allSearchParams = useAppSelector(selectAllSearchParams);

  const setPageType = (type: RootState["inbox"]["searchParams"]["t"]) => {
    dispatch(setPageMailType(type));

    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set("t", type);

    if (type === "picker") {
      urlSearchParams.delete("f");
      urlSearchParams.delete("sm");
      urlSearchParams.delete("sf");
      urlSearchParams.delete("sa");

      const pickerStatuses = allSearchParams.picker.ps;

      if (pickerStatuses?.length) {
        urlSearchParams.set("ps", pickerStatuses.join(","));
      }
    } else {
      urlSearchParams.delete("ps");
      urlSearchParams.delete("p");
    }

    const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const setPageCategory = (
    category: RootState["inbox"]["searchParams"]["f"]
  ) => {
    dispatch(setPageMailCategory(category));

    const urlSearchParams = new URLSearchParams(window.location.search);
    if (category) {
      urlSearchParams.set("f", category);
    } else {
      urlSearchParams.delete("f");
    }

    const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const setItemsPerPage = (
    itemsPerPage: (typeof inboxInitialState)["searchParams"]["itemsPerPage"]
  ) => {
    dispatch(setItemsPerPageAction(itemsPerPage));

    // Update items per page in the URL without page rerendering
    const params = new URLSearchParams(window.location.search);
    params.set("items", String(itemsPerPage));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const setMailIsSaved = (mailId: string, isSaved: boolean) => {
    dispatch(setMailIsSavedAction({ mailId, isSaved }));
  };

  const setMailStatus = (mailId: string, status: MailDataI["mailStatus"]) => {
    dispatch(setMailStatusAction({ mailId, status }));
  };

  return {
    apiEnable,
    createLabel,
    currentSelectFilter,
    decPageNumber,
    deleteLabel,
    fetchFormList,
    fetchInboxData,
    fetchInboxMailsCount,
    fetchLabels,
    fetchMailsCount,
    fetchSideInboxFiles,
    fetchSubUserList,
    formList,
    ftpEnable,
    getApiEnable,
    getCurrentPage,
    getCurrentSelectFilter,
    getFormList,
    getFTPAPIsettings,
    getFtpEnable,
    getHoveredMail,
    getIsFetching,
    getIsFetchingSideInboxFiles,
    getLabels,
    getMailsCount,
    getMailsData,
    getMailsQuantity,
    getPreviewMail,
    getPreviewMailData,
    getSearchParams,
    /**
     * @deprecated Use getSelectedMailIds instead
     */
    getSelectedList,
    getSelectedMail,
    getSideInboxFiles,
    getSorting,
    getSplitView,
    getSubUserList,
    hoveredMail,
    hoveredMailId,
    incPageNumber,
    isFetching,
    isFetchingSideInboxFiles,
    isSortAsc,
    labelMails,
    labels,
    mailsCount,
    mailsData,
    mailsQuantity,
    previewMail,
    refetchInboxData,
    removeMailForever,
    removeMailFromState,
    removeMailsAction,
    resetCurrentPage,
    searchParams,
    /**
     * @deprecated Use selectedMailIds instead
     */
    selectedList,
    selectedMail,
    selectedMailId,
    selectedMailIds,
    setCurrentSelectFilter,
    /**
     * @deprecated Use setHoveredMailId instead
     */
    setHoveredMail,
    setHoveredMailId,
    setIsFetching,
    setIsFetchingSideInboxFiles,
    setItemsPerPage,
    setLabel,
    setLabels,
    setMailDataProp,
    setMailsCount,
    setPageCategory,
    setPageNumber,
    setPageType,
    setPreviewMail,
    setPickerSearchParams,
    setSearchParams,
    /**
     * @deprecated Use setSelectedMailIds instead
     */
    setSelectedList,
    /**
     * @deprecated Use setSelectedMailId instead
     */
    setSelectedMail,
    setSelectedMailId,
    setSelectedMailIds,
    setSorting,
    setSplitView,
    sortBy,
    splitView,
    subUsersList,
    unlabelMails,
    updateLabel,
    updateLabelsCounters,
    updateMailPayments,
    updateMailSecuritySettings,
    setMailIsSaved,
    setMailStatus,
  };
};

export default useInbox;
